import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NonNullableFormBuilder } from '@angular/forms';

@Component({
  selector: 'app-date-input-field',
  templateUrl: './date-input-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DateInputFieldComponent implements ControlValueAccessor {
  @Input() autoFocus: boolean = false;
  @Output() readonly completeEvent = new EventEmitter<Date>();

  readonly dateStringControl = this.fb.control<string | null>(null);
  private lastWrittenDate: Date | null = null;
  private onChange: (value: Date) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly fb: NonNullableFormBuilder) {}

  get dateString(): string | null {
    return this.dateStringControl.getRawValue();
  }

  set dateString(val: string | null) {
    this.dateStringControl.setValue(val);
  }

  get date(): Date | null {
    return this.parseDate(this.dateString);
  }

  writeValue(date: Date | null): void {
    if (!date) return;
    // Prevent writing the same date multiple times
    if (date.valueOf() === this.lastWrittenDate?.valueOf()) return;
    this.lastWrittenDate = date;
    this.dateString = this.parseDateString(date);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onComplete(): void {
    const date = this.date;
    if (!date) return;
    this.lastWrittenDate = date;
    this.onChange(date);
    this.onTouched();
    this.completeEvent.emit(date);
  }

  private parseDateString(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private parseDate(dateString: string | null): Date | null {
    if (dateString === null) return null;
    const [day, month, year] = dateString.split('-').map(Number);
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
      return new Date(year, month - 1, day);
    }
    return null;
  }
}
